/**
 * MarketingTasks - Takenoverzicht met "Mijn taken" en "Alle taken"
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField, Checkbox, Collapse,
  IconButton, Select, MenuItem, FormControl, InputLabel, Tooltip, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon,
  Edit as EditIcon, Save as SaveIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// ─── Constants ───────────────────────────────────────────────────────────────

const TASK_STATUS_CONFIG = {
  TODO: { label: 'Te doen', color: '#6b7280' },
  IN_PROGRESS: { label: 'Bezig', color: '#2563eb' },
  WAITING: { label: 'Wachtend', color: '#f59e0b' },
  DONE: { label: 'Afgerond', color: '#059669' },
  NOT_EXECUTED: { label: 'Niet uitgevoerd', color: '#9ca3af' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isOverdue(task) {
  if (!task.dueDate || task.status === 'DONE' || task.status === 'NOT_EXECUTED') return false;
  return new Date(task.dueDate) < new Date(new Date().toDateString());
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function sortTasks(tasks) {
  return [...tasks].sort((a, b) => {
    const aOver = isOverdue(a) ? 0 : 1;
    const bOver = isOverdue(b) ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    const aDate = a.dueDate || '9999-12-31';
    const bDate = b.dueDate || '9999-12-31';
    return aDate.localeCompare(bDate);
  });
}

// ─── Task card ──────────────────────────────────────────────────────────────

function TaskCard({ task, items, onToggle, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const overdue = isOverdue(task);
  const isDone = task.status === 'DONE' || task.status === 'NOT_EXECUTED';
  const cfg = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.TODO;
  const linkedItem = items.find((i) => i.id === task.contentItemId);

  const startEdit = () => {
    setEditForm({ title: task.title || '', assigneeName: task.assigneeName || '', dueDate: task.dueDate?.slice(0, 10) || '', status: task.status || 'TODO' });
    setExpanded(true);
  };

  const saveEdit = async () => {
    if (!editForm) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      toast.success('Taak bijgewerkt');
      setEditForm(null);
      setExpanded(false);
      onUpdate?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1, p: 1.5,
        borderLeft: overdue ? '3px solid #ef4444' : '3px solid transparent',
        opacity: isDone ? 0.55 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox
          checked={isDone}
          onChange={() => onToggle(task)}
          size="small" sx={{ p: 0 }}
        />
        <Typography variant="body2" sx={{
          flex: 1, fontWeight: 500, textDecoration: isDone ? 'line-through' : 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {task.title || 'Naamloos'}
        </Typography>
        {linkedItem && (
          <Chip label={linkedItem.title} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, maxWidth: 120 }} />
        )}
        <Typography variant="caption" sx={{ color: overdue ? '#ef4444' : '#6b7280', whiteSpace: 'nowrap' }}>
          {formatDate(task.dueDate)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
          {task.assigneeName || ''}
        </Typography>
        <Chip label={cfg.label} size="small" sx={{ bgcolor: `${cfg.color}18`, color: cfg.color, fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
        <IconButton size="small" onClick={startEdit}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
      </Box>

      {/* Inline edit */}
      <Collapse in={expanded && !!editForm}>
        {editForm && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField label="Titel" size="small" value={editForm.title} onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))} sx={{ flex: 2, minWidth: 140 }} />
            <TextField label="Toegewezen aan" size="small" value={editForm.assigneeName} onChange={(e) => setEditForm((p) => ({ ...p, assigneeName: e.target.value }))} sx={{ flex: 1, minWidth: 100 }} />
            <TextField label="Deadline" type="date" size="small" value={editForm.dueDate} onChange={(e) => setEditForm((p) => ({ ...p, dueDate: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Status</InputLabel>
              <Select value={editForm.status} label="Status" onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}>
                {Object.entries(TASK_STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
            <IconButton size="small" color="primary" onClick={saveEdit}><SaveIcon sx={{ fontSize: 18 }} /></IconButton>
            <IconButton size="small" onClick={() => { setEditForm(null); setExpanded(false); }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function MarketingTasks({ tasks = [], items = [], onRefresh }) {
  const { instance } = useMsal();
  const currentEmail = instance.getActiveAccount()?.username || '';

  // Quick-add state
  const [newTitle, setNewTitle] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newItemId, setNewItemId] = useState('');

  // Section collapse
  const [myOpen, setMyOpen] = useState(true);
  const [allOpen, setAllOpen] = useState(true);

  const myTasks = useMemo(() => sortTasks(tasks.filter((t) => t.assigneeEmail === currentEmail)), [tasks, currentEmail]);
  const allTasks = useMemo(() => sortTasks(tasks), [tasks]);

  const handleToggle = useCallback(async (task) => {
    const newStatus = (task.status === 'DONE' || task.status === 'NOT_EXECUTED') ? 'TODO' : 'DONE';
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Status update mislukt');
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  }, [onRefresh]);

  const handleQuickAdd = async () => {
    if (!newTitle.trim()) { toast.error('Titel is verplicht'); return; }
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle,
          assigneeName: newAssignee,
          assigneeEmail: newAssignee ? '' : currentEmail,
          dueDate: newDueDate || null,
          contentItemId: newItemId || null,
          status: 'TODO',
        }),
      });
      if (!res.ok) throw new Error('Aanmaken mislukt');
      toast.success('Taak aangemaakt');
      setNewTitle(''); setNewAssignee(''); setNewDueDate(''); setNewItemId('');
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const itemOptions = useMemo(() => items.filter((i) => i.status !== 'ARCHIVED'), [items]);

  return (
    <Box>
      {/* Quick-add bar */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <AddIcon sx={{ color: '#6b7280' }} />
        <TextField placeholder="Nieuwe taak..." size="small" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()} sx={{ flex: 2, minWidth: 140 }} />
        <TextField label="Toegewezen aan" size="small" value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)} sx={{ flex: 1, minWidth: 100 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Content item</InputLabel>
          <Select value={newItemId} label="Content item" onChange={(e) => setNewItemId(e.target.value)}>
            <MenuItem value="">Geen</MenuItem>
            {itemOptions.map((it) => <MenuItem key={it.id} value={it.id}>{it.title}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Deadline" type="date" size="small" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
        <Button variant="contained" size="small" onClick={handleQuickAdd}>Toevoegen</Button>
      </Paper>

      {/* Mijn taken */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }} onClick={() => setMyOpen((p) => !p)}>
          {myOpen ? <CollapseIcon /> : <ExpandIcon />}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Mijn taken</Typography>
          <Chip label={myTasks.filter((t) => t.status !== 'DONE' && t.status !== 'NOT_EXECUTED').length} size="small" color="primary" />
        </Box>
        <Collapse in={myOpen}>
          {myTasks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, pl: 1 }}>Geen taken gevonden</Typography>
          )}
          {myTasks.map((t) => (
            <TaskCard key={t.id} task={t} items={items} onToggle={handleToggle} onUpdate={onRefresh} />
          ))}
        </Collapse>
      </Box>

      {/* Alle taken */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }} onClick={() => setAllOpen((p) => !p)}>
          {allOpen ? <CollapseIcon /> : <ExpandIcon />}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Alle taken</Typography>
          <Chip label={allTasks.filter((t) => t.status !== 'DONE' && t.status !== 'NOT_EXECUTED').length} size="small" />
        </Box>
        <Collapse in={allOpen}>
          {allTasks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, pl: 1 }}>Geen taken gevonden</Typography>
          )}
          {allTasks.map((t) => (
            <TaskCard key={t.id} task={t} items={items} onToggle={handleToggle} onUpdate={onRefresh} />
          ))}
        </Collapse>
      </Box>
    </Box>
  );
}
